import classNames from 'classnames';
import React, { useRef, useState, useEffect } from 'react';
import gsap from 'gsap';
import { useQuery, useMutation } from '@apollo/client';
import { useHistory, useLocation } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import { TFunction } from 'i18next';

import ImageGallery from '../../../../../components/ImageGallery/ImageGallery';
import styles from './UpsellV2.module.scss';
import * as events from '../../../../../lib/analytics/events';
import SubscriptionAppBar from '../../../components/SubscriptionAppBar';
import { IImage } from '../../../../../types';
import { Image } from '../../../../../components/Image';
import { ReactComponent as AddIcon } from '../../../../../assets/images/icons/add.svg';
import { ReactComponent as MinusIcon } from '../../../../../assets/images/icons/minus.svg';
import { ReactComponent as DigestiveIcon } from '../../../../../assets/images/icons/supplements_upsell/digestive.svg';
import { ReactComponent as BoneAndJointIcon } from '../../../../../assets/images/icons/supplements_upsell/bone-and-joint.svg';
import { ReactComponent as BrainFunctionIcon } from '../../../../../assets/images/icons/supplements_upsell/brain.svg';
import { ReactComponent as HeartHealthIcon } from '../../../../../assets/images/icons/supplements_upsell/heart.svg';
import { ReactComponent as CartilageIcon } from '../../../../../assets/images/icons/supplements_upsell/cartilage.svg';
import { ReactComponent as HipFlexibilityIcon } from '../../../../../assets/images/icons/supplements_upsell/hip-flexibility.svg';
import { ReactComponent as JointMobilityIcon } from '../../../../../assets/images/icons/supplements_upsell/joint-mobility.svg';
import { ReactComponent as RepairAndRecoveryIcon } from '../../../../../assets/images/icons/supplements_upsell/repair-and-recovery.svg';
import { ReactComponent as ShinyFurIcon } from '../../../../../assets/images/icons/supplements_upsell/shiny.svg';
import { ReactComponent as AntioxidantIcon } from '../../../../../assets/images/icons/supplements_upsell/antioxidant.svg';
import { ReactComponent as SkinBarrierIcon } from '../../../../../assets/images/icons/supplements_upsell/skin-barrier.svg';
import { ReactComponent as SensitiveSkinIcon } from '../../../../../assets/images/icons/supplements_upsell/sensitive.svg';
import { ReactComponent as StressIcon } from '../../../../../assets/images/icons/supplements_upsell/stress.svg';
import { ReactComponent as RelaxationIcon } from '../../../../../assets/images/icons/supplements_upsell/relaxation.svg';
import { ReactComponent as RestfulIcon } from '../../../../../assets/images/icons/supplements_upsell/restful.svg';
import { ReactComponent as ResilienceIcon } from '../../../../../assets/images/icons/supplements_upsell/resilience.svg';
import { ReactComponent as SkinHealthIcon } from '../../../../../assets/images/icons/supplements_upsell/skin-health.svg';
import { ReactComponent as ExerciseAndActivityIcon } from '../../../../../assets/images/icons/supplements_upsell/exercise.svg';
import { ReactComponent as KidneyAndLiverIcon } from '../../../../../assets/images/icons/supplements_upsell/kidney.svg';
import { ReactComponent as StarIcon } from '../../../../../assets/images/icons/star.svg';
import { ReactComponent as ArrowAngleIcon } from '../../../../../assets/images/icons/arrow_angle.svg';
import { ReactComponent as ReturnsIcon } from '../../../../../assets/images/icons/returns.svg';
import { ReactComponent as ShippingIcon } from '../../../../../assets/images/icons/shipping.svg';
import { ReactComponent as MuscleIcon } from '../../../../../assets/images/icons/supplements_upsell/muscle.svg';
import FAQ from '../../../../Checkout/components/Supplements/ProductInfo/FAQ';
import DeliveryFrequencyModal from '../DeliveryFrequencyModal/DeliveryFrequencyModal';
import InAppCheckoutModal from '../InAppCheckoutModal/InAppCheckoutModal';
import * as types from '../../../../../types';
import { formattedCentsToDollars, getLoadingOrErrorElement } from '../../../../../lib/util';
import {
  addMultipleSupplementSubscriptionsMutation,
  supplementsSubscriptionsQuery,
} from '../../../../../graphql-operations';
import AppPaths from '../../../../../AppPaths';
import { ChangeType, SplashPageState, Toast } from '../../SplashPage';
import { ConfirmModal } from '../../../../../components/modals';
import { getFiGrowthBook } from '../../../../../lib/growthbook';
import { useSelector } from 'react-redux';

export enum SKUType {
  Formula = '8-in-1 Formula',
  HipJoint = 'Hip & Joint',
  SkinCoat = 'Skin & Coat',
  Calming = 'Calming',
}

export enum PurchaseType {
  Autoship = 'Autoship',
  OneTime = 'One Time',
}

interface UpsellProps {
  referrer?: 'add' | 'upsell';
  closeUpsell?: () => void;
}

export interface SupplementCartItem {
  skuType: SKUType;
  purchaseType: PurchaseType;
  eligibleForDiscount: boolean;
  frequency?: number;
}

export const SUPPLEMENT_BAG_IMAGES: Record<SKUType, IImage> = {
  [SKUType.Formula]: {
    type: 'image',
    url: '/product_images/supplements/upsell-v2/formula-bag.png',
  },
  [SKUType.Calming]: {
    type: 'image',
    url: '/product_images/supplements/upsell-v2/calming-bag.png',
  },
  [SKUType.HipJoint]: {
    type: 'image',
    url: '/product_images/supplements/upsell-v2/hipjoint-bag.png',
  },
  [SKUType.SkinCoat]: {
    type: 'image',
    url: '/product_images/supplements/upsell-v2/skincoat-bag.png',
  },
};

export const getSupplementKeyBenefits = (
  t: TFunction,
): Record<SKUType, Array<{ image: JSX.Element; title: string; description: string }>> => ({
  [SKUType.Formula]: [
    {
      image: <DigestiveIcon />,
      title: t('digestive_system'),
      description: t('turmeric_root_powder_and_thiamin_support_normal_gut_function'),
    },
    {
      image: <BoneAndJointIcon />,
      title: t('bone_joint'),
      description: t('glucosamine_msm_and_chondroitin_maintain_bone_health_and_joint_mobility'),
    },
    {
      image: <BrainFunctionIcon />,
      title: t('brain_function'),
      description: t('thiamin_vitamin_b6_and_vitamin_b12_support_cognitive_functioning'),
    },
    {
      image: <HeartHealthIcon />,
      title: t('heart_health'),
      description: t('vitamin_c_and_zinc_support_the_cardiovascular_system'),
    },
  ],
  [SKUType.HipJoint]: [
    {
      image: <CartilageIcon />,
      title: t('cartilage_health'),
      description: t('vitamin_c_helps_maintain_strong_connective_tissue'),
    },
    {
      image: <HipFlexibilityIcon />,
      title: t('hip_flexibility'),
      description: t('green_lipped_mussel_provides_glycosaminoglycans_gags_to_support_normal_range_of_motion'),
    },
    {
      image: <JointMobilityIcon />,
      title: t('joint_mobility'),
      description: t('krill_glucosamine_hcl_chondroitin_and_msm_enhance_joint_health_and_reduce_occasional_stiffness'),
    },
    {
      image: <RepairAndRecoveryIcon />,
      title: t('repair_recovery'),
      description: t('zinc_supports_natural_healing_processes_and_overall_immune_function'),
    },
  ],
  [SKUType.SkinCoat]: [
    {
      image: <ShinyFurIcon />,
      title: t('shiny_healthy_fur'),
      description: t('omega_3_fatty_acids_from_fish_oil_promote_soft_glossy_coats'),
    },
    {
      image: <AntioxidantIcon />,
      title: t('antioxidant_support'),
      description: t('vitamin_c_and_vitamin_e_help_maintain_healthy_skin_cells_and_normal_collagen_production'),
    },
    {
      image: <SkinBarrierIcon />,
      title: t('skin_barrier_health'),
      description: t('biotin_and_zinc_help_maintain_skin_barrier_to_protect_against_environmental_irritants'),
    },
    {
      image: <SensitiveSkinIcon />,
      title: t('sensitive_skin'),
      description: t('essential_vitamins_and_nutrients_promote_normal_inflammatory_responses'),
    },
  ],
  [SKUType.Calming]: [
    {
      image: <StressIcon />,
      title: t('stress_anxiety_management'),
      description: t('valerian_root_and_l_theanine_promote_relaxation_and_healthy_nervous_system_function'),
    },
    {
      image: <RelaxationIcon />,
      title: t('relaxation_without_sedation'),
      description: t('cbd_and_magnesium_support_calmness_while_maintaining_alertness'),
    },
    {
      image: <RestfulIcon />,
      title: t('restful_sleep'),
      description: t('melatonin_and_l_tryptophan_promote_restorative_naps_and_sleep'),
    },
    {
      image: <ResilienceIcon />,
      title: t('resilience_in_stressful_situations'),
      description: t('thiamine_helps_them_maintain_composure_during_anxiety_inducing_events'),
    },
  ],
});

const getAllBenefits = (
  t: any,
): Record<
  SKUType,
  { benefits: Array<{ image: JSX.Element; title: string; description: string }>; ingredients: string }
> => ({
  [SKUType.Formula]: {
    benefits: [
      {
        image: <DigestiveIcon />,
        title: t('digestive_system'),
        description: t('turmeric_root_powder_and_thiamin_support_normal_gut_function'),
      },
      {
        image: <BoneAndJointIcon />,
        title: t('bone_joint'),
        description: t('glucosamine_msm_and_chondroitin_maintain_bone_health_and_joint_mobility'),
      },
      {
        image: <BrainFunctionIcon />,
        title: t('brain_function'),
        description: t('thiamin_vitamin_b6_and_vitamin_b12_support_cognitive_functioning'),
      },
      {
        image: <HeartHealthIcon />,
        title: t('heart_health'),
        description: t('vitamin_c_and_zinc_support_the_cardiovascular_system'),
      },
      {
        image: <MuscleIcon />,
        title: t('muscle_growth'),
        description: t('vitamin_b6_vitamin_b12_and_vitamin_d_support_physical_performance'),
      },
      {
        image: <SkinHealthIcon />,
        title: t('skin_health'),
        description: t('biotin_zinc_and_collagen_support_skin_cell_health'),
      },
      {
        image: <ExerciseAndActivityIcon />,
        title: t('exercise_activity'),
        description: t('manganese_and_zinc_help_maintain_normal_metabolism_and_nervous_system_processing'),
      },
      {
        image: <KidneyAndLiverIcon />,
        title: t('kidney_liver'),
        description: t('turmeric_root_and_vitamin_e_promote_overall_well_being'),
      },
    ],
    ingredients: t('ingredients_formula'),
  },
  [SKUType.HipJoint]: {
    benefits: [
      {
        image: <CartilageIcon />,
        title: t('cartilage_health'),
        description: t('vitamin_c_helps_maintain_strong_connective_tissue'),
      },
      {
        image: <HipFlexibilityIcon />,
        title: t('hip_flexibility'),
        description: t('green_lipped_mussel_provides_glycosaminoglycans_gags_to_support_normal_range_of_motion'),
      },
      {
        image: <JointMobilityIcon />,
        title: t('joint_mobility'),
        description: t(
          'krill_glucosamine_hcl_chondroitin_and_msm_enhance_joint_health_and_reduce_occasional_stiffness',
        ),
      },
      {
        image: <RepairAndRecoveryIcon />,
        title: t('repair_recovery'),
        description: t('zinc_supports_natural_healing_processes_and_overall_immune_function'),
      },
    ],
    ingredients: t('ingredients_hip_joint'),
  },
  [SKUType.SkinCoat]: {
    benefits: [
      {
        image: <ShinyFurIcon />,
        title: t('shiny_healthy_fur'),
        description: t('omega_3_fatty_acids_from_fish_oil_promote_soft_glossy_coats'),
      },
      {
        image: <AntioxidantIcon />,
        title: t('antioxidant_support'),
        description: t('vitamin_c_and_vitamin_e_help_maintain_healthy_skin_cells_and_normal_collagen_production'),
      },
      {
        image: <SkinBarrierIcon />,
        title: t('skin_barrier_health'),
        description: t('biotin_and_zinc_help_maintain_skin_barrier_to_protect_against_environmental_irritants'),
      },
      {
        image: <SensitiveSkinIcon />,
        title: t('sensitive_skin'),
        description: t('essential_vitamins_and_nutrients_promote_normal_inflammatory_responses'),
      },
    ],
    ingredients: t('ingredients_skin_coat'),
  },
  [SKUType.Calming]: {
    benefits: [
      {
        image: <StressIcon />,
        title: t('stress_anxiety_management'),
        description: t('valerian_root_and_l_theanine_promote_relaxation_and_healthy_nervous_system_function'),
      },
      {
        image: <RelaxationIcon />,
        title: t('relaxation_without_sedation'),
        description: t('cbd_and_magnesium_support_calmness_while_maintaining_alertness'),
      },
      {
        image: <RestfulIcon />,
        title: t('restful_sleep'),
        description: t('melatonin_and_l_tryptophan_promote_restorative_naps_and_sleep'),
      },
      {
        image: <ResilienceIcon />,
        title: t('resilience_in_stressful_situations'),
        description: t('thiamine_helps_them_maintain_composure_during_anxiety_inducing_events'),
      },
    ],
    ingredients: t('ingredients_calming'),
  },
});

const RECURLY_ERROR_ADDENDUM = 'Please use a different card or contact your bank.'; // This is appended by Recurly, not user-facing

export const AllBenefits: React.FC<{ skuType: SKUType }> = ({ skuType }) => {
  const { t } = useTranslation();
  const { benefits, ingredients } = getAllBenefits(t)[skuType];

  return (
    <div className={styles.benefitContainer}>
      <div className={styles.benefitContainerSubtitle}>
        {skuType} {t('benefits')}
      </div>
      <div className={styles.benefitContainerTitle}>{t('formulated_for')}</div>
      <div className={styles.benefitItemContainer}>
        {benefits.map((benefit, index) => (
          <div key={index} className={styles.benefitItem}>
            <div className={styles.benefitImage}>{benefit.image}</div>
            <div className={styles.benefitText}>
              <div className={styles.benefitTitle}>{benefit.title}</div>
              <div className={styles.benefitDescription}>{benefit.description}</div>
            </div>
          </div>
        ))}
      </div>
      <div className={styles.ingredientsTitle}>{t('whats_inside')}</div>
      <div className={styles.ingredients}>
        <span className={styles.ingredientsBold}>{t('all_ingredients')} </span>
        {ingredients}
      </div>
      <div className={styles.ingredientsDisclaimer}>
        {t('this_product_is_not_intended_to_diagnose_treat_cure_or_prevent_any_disease')}
      </div>
    </div>
  );
};

const KeyBenefits: React.FC<{
  selectedSKUType: SKUType;
  skuType: SKUType;
}> = ({ selectedSKUType, skuType }) => {
  const { t } = useTranslation();
  const initialSKUType = getInitialSKUTypeFromURL();
  const [keyBenefitsExpanded, setKeyBenefitsExpanded] = useState<boolean>(initialSKUType === skuType);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (containerRef.current) {
      if (keyBenefitsExpanded) {
        gsap.to(containerRef.current, { height: 'auto', opacity: 1, duration: 0.3 });
      } else {
        gsap.to(containerRef.current, { height: 0, opacity: 0, duration: 0.3 });
      }
    }
  }, [keyBenefitsExpanded]);

  useEffect(() => {
    if (selectedSKUType !== skuType) {
      setKeyBenefitsExpanded(false);
    }
  }, [selectedSKUType, skuType]);

  return (
    <div>
      <div
        ref={containerRef}
        style={{ overflow: 'hidden', height: 0, opacity: 0 }}
        className={styles.skuOptionKeyBenefitContainer}
      >
        <div className={styles.skuOptionKeyBenefitCollapseButton} onClick={() => setKeyBenefitsExpanded(false)}>
          <MinusIcon /> {t('collapse_key_benefits')}
        </div>
        {getSupplementKeyBenefits(t)[selectedSKUType].map((benefit, index) => (
          <div key={index} className={styles.skuOptionKeyBenefitItem}>
            <div className={styles.skuOptionKeyBenefitItemImageContainer}>{benefit.image}</div>
            <div className={styles.skuOptionKeyBenefitItemText}>
              <div className={styles.skuOptionKeyBenefitItemTitle}>{benefit.title}</div>
              <div className={styles.skuOptionKeyBenefitItemDescription}>{benefit.description}</div>
            </div>
          </div>
        ))}
      </div>
      {selectedSKUType === skuType && !keyBenefitsExpanded && (
        <div className={styles.skuOptionKeyBenefitExpandButton} onClick={() => setKeyBenefitsExpanded(true)}>
          <AddIcon /> {t('see_key_benefits')}
        </div>
      )}
    </div>
  );
};

export const getSKUOptionDetails = (t: TFunction): Record<SKUType, { title: string; description: string }> => ({
  [SKUType.Formula]: {
    title: t('8_in_1_formula'),
    description: t('covers_the_main_areas_of_your_dogs_health'),
  },
  [SKUType.HipJoint]: {
    title: t('hip_joint'),
    description: t('maintains_mobility_and_flexibility'),
  },
  [SKUType.SkinCoat]: {
    title: t('skin_coat'),
    description: t('promotes_a_vibrant_and_healthy_coat'),
  },
  [SKUType.Calming]: {
    title: t('calming'),
    description: t('supports_relaxation_and_resilience'),
  },
});

export const SKUOption: React.FC<{
  skuType: SKUType;
  selectedSKUType: SKUType;
  onSelect: (skuType: SKUType) => void;
  fromInsights?: boolean;
  petName?: string;
  recommended?: SKUType;
  imageOnly?: boolean;
}> = ({
  skuType,
  selectedSKUType,
  onSelect,
  fromInsights = false,
  petName = 'your dog',
  recommended = SKUType.Formula,
  imageOnly = false,
}) => {
  const { t } = useTranslation();
  const { title, description } = getSKUOptionDetails(t)[skuType];
  return (
    <>
      {fromInsights && recommended === skuType && (
        <span
          className={classNames(styles.skuRecommended, {
            [styles.skuRecommendedSelected]: selectedSKUType === skuType,
          })}
          onClick={() => onSelect(skuType)}
        >
          {t('recommended_for_pet_name', { petName })}
        </span>
      )}
      <div
        className={classNames(styles.skuOption, {
          [styles.skuOptionSelected]: selectedSKUType === skuType,
          [styles.insights]: fromInsights && recommended === skuType,
          [styles.imageOnly]: imageOnly,
        })}
        onClick={() => onSelect(skuType)}
      >
        {imageOnly && <Image className={styles.skuOptionImage} image={SUPPLEMENT_BAG_IMAGES[skuType]} />}
        {!imageOnly && (
          <>
            <div className={styles.skuOptionContentContainer}>
              <Image className={styles.skuOptionImage} image={SUPPLEMENT_BAG_IMAGES[skuType]} />
              <div className={styles.skuOptionText}>
                <div className={styles.skuOptionTitle}>{title}</div>
                <div className={styles.skuOptionDescription}>{description}</div>
              </div>
            </div>
            <KeyBenefits selectedSKUType={selectedSKUType} skuType={skuType} />
          </>
        )}
      </div>
    </>
  );
};

export const Reviews: React.FC = () => {
  const { t } = useTranslation();
  return (
    <div className={styles.reviewsContainer}>
      <div className={styles.reviewsTitle}>{t('what_theyre_saying')}</div>
      <div className={styles.reviewsTitleDesktop}>{t('reviews')}</div>
      <div className={styles.heroReviewItem}>
        <div className={styles.reviewRating}>
          <StarIcon />
          <StarIcon />
          <StarIcon />
          <StarIcon />
          <StarIcon />
        </div>
        <div className={styles.reviewText}>{t('this_super_picky_doodle_is_a_big_fan')}</div>
        <div className={styles.reviewTextDesktop}>{t('super_picky_doodle_is_a_big_fan')}</div>
      </div>
      <div className={styles.smallReviewContainer}>
        <div className={styles.reviewItem}>
          <div className={styles.reviewRating}>
            <StarIcon />
            <StarIcon />
            <StarIcon />
            <StarIcon />
            <StarIcon />
          </div>
          <div className={styles.reviewText}>
            <Trans
              i18nKey="with_fi_supplements_we_notice_an_incredible_increase_in_energy_and_excitement_if_we_skip_a_meal_or_two_and_forget_we_can_tell_this_is_a_game_changer_for_overweight_dogs_that_need_a_little_energy"
              components={{ 0: <strong /> }}
            />
          </div>
        </div>
        <div className={styles.reviewItem}>
          <div className={styles.reviewRating}>
            <StarIcon />
            <StarIcon />
            <StarIcon />
            <StarIcon />
            <StarIcon />
          </div>
          <div className={styles.reviewText}>
            <Trans
              i18nKey="my_dogs_are_really_picky_with_what_vitamins_they_eat_these_they_lap_up_as_if_its_their_favorite_treat"
              components={{ 0: <strong /> }}
            />
          </div>
        </div>
        <div className={styles.reviewItem}>
          <div className={styles.reviewRating}>
            <StarIcon />
            <StarIcon />
            <StarIcon />
            <StarIcon />
            <StarIcon />
          </div>
          <div className={styles.reviewText}>
            <Trans
              i18nKey="we_tried_chewables_before_but_luna_would_just_taste_them_and_then_spit_them_out_so_we_needed_a_powder_that_we_could_mix_up_into_her_food_so_far_she_hasnt_noticed_a_thing_and_has_even_started_eating_the_food_we_werent_sure_she_liked"
              components={{ 0: <strong /> }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export const PurchaseContainer: React.FC<{
  purchaseType: PurchaseType | null;
  setPurchaseType: React.Dispatch<React.SetStateAction<PurchaseType | null>>;
  autoshipFrequency: number;
  setAutoshipFrequency: React.Dispatch<React.SetStateAction<number>>;
  isNextItemEligibleForFirstBagDiscount: boolean;
  isEligibleForFreeBag: boolean;
  fiveDollarFirstBagOfferLive: boolean;
  autoshipOnly?: boolean;
  fromInsights?: boolean;
  petName?: string;
}> = ({
  purchaseType,
  setPurchaseType,
  autoshipFrequency,
  setAutoshipFrequency,
  isNextItemEligibleForFirstBagDiscount,
  isEligibleForFreeBag,
  fiveDollarFirstBagOfferLive,
  autoshipOnly = false,
  fromInsights = false,
  petName = 'your dog',
}) => {
  const { t } = useTranslation();
  return (
    <div className={styles.purchaseContainer}>
      {!autoshipOnly && <div className={styles.purchaseTitle}>{t('how_do_you_want_to_purchase')}</div>}
      {!autoshipOnly && (
        <div className={styles.purchaseOptionContainer}>
          <div
            className={classNames(styles.buyOncepurchaseOption, {
              [styles.purchaseOptionSelected]: purchaseType === PurchaseType.OneTime,
            })}
            onClick={() => setPurchaseType(PurchaseType.OneTime)}
          >
            <div className={styles.purchaseOptionText}>
              <div className={styles.purchaseOptionTitle}>{t('buy_once')}</div>
            </div>
            <div className={styles.purchaseOptionPrice}>{'$30'}</div>
          </div>
          <div
            className={classNames(styles.autoshipPurchaseOption, {
              [styles.purchaseOptionSelected]: purchaseType === PurchaseType.Autoship,
            })}
            onClick={() => setPurchaseType(PurchaseType.Autoship)}
          >
            {isNextItemEligibleForFirstBagDiscount && fiveDollarFirstBagOfferLive && (
              <div className={styles.purchaseOptionAutoshipUpsell}>
                {t('first_bag_special_extra_20_off_applied_at_checkout')}
              </div>
            )}
            {!fiveDollarFirstBagOfferLive && isEligibleForFreeBag && (
              <div className={styles.purchaseOptionAutoshipUpsell}>
                {t('limited_time_offer_get_your_first_bag_free')}
              </div>
            )}
            <div className={styles.autoshipPurchaseOptionContent}>
              <div className={styles.purchaseOptionText}>
                <div className={styles.purchaseOptionTitleAutoship}>{t('auto_ship')}</div>
                <div className={styles.purchaseOptionDescription}>
                  {t('subscribe_and_save')} {!fiveDollarFirstBagOfferLive && t('amount_per_bag', { amount: '5' })}
                </div>
              </div>

              {fiveDollarFirstBagOfferLive && isNextItemEligibleForFirstBagDiscount ? (
                <div className={styles.purchaseOptionPrice}>
                  {'$5'} <span className={styles.originalPurchaseOptionPrice}>{'$25'}</span>
                </div>
              ) : (
                <div className={styles.purchaseOptionPrice}>{'$25'}</div>
              )}
            </div>
          </div>
        </div>
      )}
      <div className={styles.whatIsRightContainer} onClick={() => {}}>
        <div className={styles.whatIsRightTitle}>{t('whats_inside')}</div>
        <div className={styles.whatIsRightIcon}>
          <AddIcon />
        </div>
      </div>
      {purchaseType === PurchaseType.Autoship && (
        <>
          <div className={styles.frequencyTitle}>
            {fromInsights
              ? t('how_often_do_you_want_deliveries_for_pet_name', { petName })
              : autoshipOnly
              ? t('auto_ship_frequency')
              : t('select_your_delivery_frequency')}
          </div>
          <div className={styles.frequencyContainer}>
            <button
              className={classNames(styles.frequencyMinusButton, {
                [styles.frequencyMinusButtonDisabled]: autoshipFrequency === 1,
              })}
              onClick={() => setAutoshipFrequency(Math.max(autoshipFrequency - 1, 1))}
            >
              <MinusIcon />
            </button>
            <div className={styles.frequencyValue}>
              {autoshipFrequency}{' '}
              <span className={styles.frequencyValueUnit}>{t('weeks_weeks', { count: autoshipFrequency })}</span>
            </div>
            <button
              className={classNames(styles.frequencyPlusButton, {
                [styles.frequencyPlusButtonDisabled]: autoshipFrequency === 12,
              })}
              onClick={() => setAutoshipFrequency(Math.min(autoshipFrequency + 1, 12))}
            >
              <AddIcon />
            </button>
          </div>
          {autoshipOnly && !fiveDollarFirstBagOfferLive && (
            <div className={styles.autoshipPricing}>
              {t('autoship_pricing', {
                weeklyPrice: formattedCentsToDollars(2500 / autoshipFrequency),
                totalPrice: formattedCentsToDollars(2500),
                weeks: autoshipFrequency,
              })}
            </div>
          )}
          {fromInsights ? (
            <p>
              <Trans
                i18nKey="we_suggest_every_6_weeks_based_on_pet_name_breed_size_and_activity_level_feel_free_to_adjust"
                values={{ petName }}
                components={{ 0: <strong /> }}
              />
            </p>
          ) : (
            <DeliveryFrequencyModal
              onContinue={(frequency: number) => setAutoshipFrequency(frequency)}
              trigger={
                <div className={styles.whatIsRightContainer}>
                  <div className={styles.whatIsRightTitle}>{t('what_is_right_for_your_dog')}</div>
                  <div className={styles.whatIsRightIcon}>
                    <ArrowAngleIcon />
                  </div>
                </div>
              }
            />
          )}
          <div className={styles.freeGroundShippingContainer}>
            <div className={styles.freeGroundShippingIcon}>
              <ShippingIcon />
            </div>
            <div className={styles.freeGroundShippingTitle}>{t('free_ground_shipping')}</div>
          </div>
          <div className={styles.tryRiskFreeContainer}>
            <div className={styles.tryRiskFreeIcon}>
              <ReturnsIcon />
            </div>
            <div className={styles.tryRiskFreeTitle}>{t('try_risk_free_cancel_anytime')}</div>
          </div>
        </>
      )}
    </div>
  );
};

const baseSkuMap: Record<SKUType, string> = {
  [SKUType.Formula]: 'f2-v-mv-180',
  [SKUType.HipJoint]: 'f1-v-hj-180',
  [SKUType.SkinCoat]: 'f1-v-sc-180',
  [SKUType.Calming]: 'f1-v-cc-180',
};

export function getSKUForCartItem(item: SupplementCartItem): string {
  const baseSku = baseSkuMap[item.skuType];
  if (item.purchaseType === PurchaseType.OneTime) {
    return baseSku;
  }

  const frequency = `${item.frequency!}-week`;
  return `${baseSku}-${frequency}-no-trial`;
}

export function getCartSKUs(cartItems: SupplementCartItem[]): string[] {
  return cartItems.map((item) => getSKUForCartItem(item));
}

// Returns associated image index for supplements PDP
export const skuIntegerImageMap: Record<SKUType, number> = {
  [SKUType.Formula]: 1,
  [SKUType.HipJoint]: 2,
  [SKUType.SkinCoat]: 3,
  [SKUType.Calming]: 4,
};

// Returns associated image index for supplements upsell
export const skuToImageIndex: Record<SKUType, number> = {
  [SKUType.Formula]: 1,
  [SKUType.SkinCoat]: 2,
  [SKUType.Calming]: 3,
  [SKUType.HipJoint]: 4,
};

export const urlTypeParamToSkuType: Record<string, SKUType> = {
  '8-in-1_formula': SKUType.Formula,
  hip_joint: SKUType.HipJoint,
  skin_coat: SKUType.SkinCoat,
  calming: SKUType.Calming,
};

export function getInitialSKUTypeFromURL(): SKUType | null {
  const url = new URL(window.location.href);
  const skuType = urlTypeParamToSkuType[url.searchParams.get('type') ?? ''];
  return skuType ? skuType : null;
}

export default function Upsell({ referrer, closeUpsell }: UpsellProps) {
  const { t } = useTranslation();
  const {
    data: subscriptionData,
    loading: subscriptionLoading,
    error: subscriptionError,
  } = useQuery<types.gqlTypes.ECOMMERCE_getSupplementSubscriptionsQuery>(supplementsSubscriptionsQuery, {
    fetchPolicy: 'cache-and-network',
  });

  const location = useLocation<SplashPageState>();
  const [showToast, setShowToast] = useState(false);
  const [toastChange, setToastChange] = useState<ChangeType | null>(null);
  const locationToastChange = location.state?.toast?.change ?? null;

  useEffect(() => {
    if (locationToastChange) {
      setShowToast(true);
      setToastChange(locationToastChange);
    }
  }, [locationToastChange]);

  const freeBagOfferLive = useSelector((state: types.AppState) => state.config.siteConfig.firstBagFree ?? false);

  const initialSkuType = getInitialSKUTypeFromURL() ?? SKUType.Formula;
  const [selectedSKUType, setSelectedSKUType] = useState<SKUType>(initialSkuType);
  const [recommendedSkuType, _setRecommendedSkuType] = useState<SKUType>(initialSkuType);
  const [purchaseType, setPurchaseType] = useState<PurchaseType | null>(PurchaseType.Autoship);
  const [autoshipFrequency, setAutoshipFrequency] = useState<number>(4);
  const [fromInsights, setFromInsights] = useState<boolean>(false);
  const [petName, setPetName] = useState<string>('');
  const [initialImageCarouselNumber, setInitialImageCarouselNumber] = useState<number>(0);

  useEffect(() => {
    const url = new URL(window.location.href);

    // If insight URL param is set to true, that means users are coming to this page from supplement insights in-app.
    const insightParam = url.searchParams.get('insight');
    const insights = insightParam ? JSON.parse(insightParam) : false;
    const nameParam = url.searchParams.get('pet_name') ?? 'your dog';
    const MAX_NAME_LENGTH = 16;
    const name = nameParam.length <= MAX_NAME_LENGTH ? nameParam : 'your dog';
    setFromInsights(insights);
    setPetName(name);
    insights && setAutoshipFrequency(6);
    // If user is coming from insights, carousel image should default to the image that matches the preselected type.
    insights && setInitialImageCarouselNumber(skuToImageIndex[initialSkuType]);
  }, [initialSkuType]);

  useEffect(() => {
    const url = new URL(window.location.href);

    url.searchParams.set(
      'type',
      Object.keys(urlTypeParamToSkuType).find((key) => urlTypeParamToSkuType[key] === selectedSKUType) || '',
    );
    window.history.replaceState({}, '', url.toString());
  }, [selectedSKUType]);

  const [cartModalOpen, setCartModalOpen] = useState<boolean>(false);
  const [cartItems, setCartItems] = useState<SupplementCartItem[]>([]);
  const fiveDollarFirstBagOfferLiveInApp = getFiGrowthBook().getFeatureValue<boolean>(
    'supplements-five-dollar-first-bag-in-app',
    false,
  );
  const isEligibleForFreeBag =
    !fiveDollarFirstBagOfferLiveInApp &&
    freeBagOfferLive &&
    !subscriptionData?.currentUser.hasPurchasedSupplementSubscription;
  const isEligibleForFirstBagDiscount = fiveDollarFirstBagOfferLiveInApp
    ? subscriptionData?.currentUser.billingAccount?.subscriptions.length === 0
    : isEligibleForFreeBag;
  const cartContainsDiscountEligibleItems = cartItems.some((item) => item.eligibleForDiscount);
  const isNextItemEligibleForFirstBagDiscount = isEligibleForFirstBagDiscount && !cartContainsDiscountEligibleItems;

  const handleRemoveItem = (index: number, close: () => void) => {
    const updatedCartItems = cartItems.filter((_, i) => i !== index);
    setCartItems(updatedCartItems);

    if (updatedCartItems.length === 0) {
      close();
    }
  };
  const userHasPaymentInfo = subscriptionData?.currentUser.billingAccount?.billingInfo !== null && !subscriptionError;

  const [billingError, setBillingError] = useState<string | null>(null);

  const history = useHistory();
  const goToSupplementsHomeWithSuccess = (refetch: boolean) => {
    setCartItems([]);
    closeUpsell && closeUpsell();
    history.push({
      pathname: AppPaths.Supplements.Root,
      search: window.location.search,
      state: { toast: { success: true, change: 'added' }, refetch },
    });
  };

  const [purchaseMutation, purchaseMutationState] = useMutation<
    types.gqlTypes.ECOMMERCE_addMultipleSupplementSubscriptions,
    types.gqlTypes.ECOMMERCE_addMultipleSupplementSubscriptionsVariables
  >(addMultipleSupplementSubscriptionsMutation, {
    refetchQueries: [{ query: supplementsSubscriptionsQuery }],
    onCompleted: (mutationData) => {
      const success = !!mutationData.addMultipleSupplementSubscriptions?.success;
      if (!success) {
        throw new Error(
          mutationData.addMultipleSupplementSubscriptions?.reasonForFailure ??
            t('unable_to_purchase_subscription_please_contact_customer_support'),
        );
      } else {
        events.supplements.purchaseSucceeded('in-app', fiveDollarFirstBagOfferLiveInApp);
        goToSupplementsHomeWithSuccess(false);
      }
    },
  });

  const loadingOrErrorElement = getLoadingOrErrorElement(subscriptionLoading, null);
  if (loadingOrErrorElement) {
    return loadingOrErrorElement;
  }
  const purchaseLoading = getLoadingOrErrorElement(purchaseMutationState.loading, null);
  if (purchaseLoading) {
    return purchaseLoading;
  }

  return (
    <>
      <SubscriptionAppBar
        title={t('supplements')}
        backButtonExitsWebview={referrer === 'upsell' ? true : false}
        backButtonAnalyticsEvent={() => events.supplementsManagement.goBackFromPage({}, 'Upsell')}
        closeButtonAnalyticsEvent={() => events.supplementsManagement.closePage({}, 'Upsell')}
        forceBackButtonBehavior={referrer === 'add' ? closeUpsell : undefined}
        noCloseButton={true}
        shoppingBagValue={cartItems.length}
        shoppingBagOnClick={() => setCartModalOpen(true)}
      />
      <div className={classNames('supplements-pdp', styles.productInfo)}>
        <div className={classNames(styles.imageGalleryContainer)}>
          <ImageGallery
            media={[
              {
                type: 'image',
                url: `/product_images/supplements/upsell-v2/supplements-in-app-1.png`,
                retinaWidth: 1200,
                retinaHeight: 1200,
              },
              {
                type: 'image',
                url: `/product_images/supplements/upsell-v2/supplements-in-app-2.png`,
                retinaWidth: 1200,
                retinaHeight: 1200,
              },
              {
                type: 'image',
                url: `/product_images/supplements/upsell-v2/supplements-in-app-3.png`,
                retinaWidth: 1200,
                retinaHeight: 1200,
              },
              {
                type: 'image',
                url: `/product_images/supplements/upsell-v2/supplements-in-app-4.png`,
                retinaWidth: 1200,
                retinaHeight: 1200,
              },
              {
                type: 'image',
                url: `/product_images/supplements/upsell-v2/supplements-in-app-5.png`,
                retinaWidth: 1200,
                retinaHeight: 1200,
              },
            ]}
            squareAspectRatio
            padGallerySelectorImages
            pageDots
            defaultImageIndex={initialImageCarouselNumber}
          />
          {isEligibleForFreeBag && (
            <div className={styles.highlightPromo}>
              {/* eslint-disable-next-line react/jsx-no-literals */}
              <span className={styles.limitedTime}>{t('limited_time_offer')} –&nbsp;</span>
              {t('first_bag_is_free')}
            </div>
          )}
          <h1 className={classNames(styles.titleText, { [styles.pad]: !isEligibleForFreeBag })}>
            {t('fi_supplements')}
          </h1>
          <p className={styles.descriptionText}>
            {fromInsights
              ? t('give_pet_name_the_extra_support_they_need_to_feel_great_every_day_with_our_proven_formulas', {
                  petName,
                })
              : t('data_backed_targeted_formulas_for_smart_daily_dog_care')}
          </p>
          <div className={styles.skuOptionSelector}>
            {Object.values(SKUType).map((skuType) => (
              <SKUOption
                key={skuType}
                skuType={skuType}
                selectedSKUType={selectedSKUType}
                onSelect={setSelectedSKUType}
                fromInsights={fromInsights}
                petName={petName}
                recommended={recommendedSkuType}
              />
            ))}
          </div>
          <div className={styles.divider} />
          <PurchaseContainer
            purchaseType={purchaseType}
            setPurchaseType={setPurchaseType}
            autoshipFrequency={autoshipFrequency}
            setAutoshipFrequency={setAutoshipFrequency}
            isNextItemEligibleForFirstBagDiscount={isNextItemEligibleForFirstBagDiscount}
            fromInsights={fromInsights}
            petName={petName}
            fiveDollarFirstBagOfferLive={fiveDollarFirstBagOfferLiveInApp}
            isEligibleForFreeBag={isEligibleForFreeBag}
          />
        </div>
        <div className={styles.divider} />
        <AllBenefits skuType={selectedSKUType} />
        <div className={styles.divider} />
        <Reviews />
        <div className={styles.divider} />
        <FAQ />
      </div>
      <ConfirmModal
        open={!!billingError}
        body={
          <>
            <h4>{t('there_was_an_issue_with_your_payment')}</h4>
            <p>{billingError}</p>
          </>
        }
        trigger={<></>}
        acceptText={t('update_payment')}
        onAccept={() => {
          history.push({
            pathname: AppPaths.Supplements.CheckoutAddPayment,
            search: window.location.search,
            state: {
              isEligibleForFirstBagDiscount,
              purchaseType,
              autoshipFrequency,
              selectedSKUType,
              fiveDollarFirstBagOfferLiveInApp,
              isEligibleForFreeBag,
            },
          });
        }}
      />
      {!userHasPaymentInfo && (
        <div className={styles.addToCartContainer}>
          <button
            className={styles.addToCartButton}
            onClick={() => {
              history.push({
                pathname: AppPaths.Supplements.CheckoutAddPayment,
                search: window.location.search,
                state: {
                  isEligibleForFirstBagDiscount,
                  purchaseType,
                  autoshipFrequency,
                  selectedSKUType,
                  fiveDollarFirstBagOfferLiveInApp,
                  isEligibleForFreeBag,
                },
              });
            }}
          >
            {t('next')}
          </button>
        </div>
      )}
      {userHasPaymentInfo && (
        <InAppCheckoutModal
          open={cartModalOpen}
          onClose={() => setCartModalOpen(false)}
          cartItems={cartItems}
          fiveDollarFirstBagOfferLive={fiveDollarFirstBagOfferLiveInApp}
          isEligibleForFreeBag={isEligibleForFreeBag}
          trigger={
            <div className={styles.addToCartContainer}>
              <button
                className={styles.addToCartButton}
                onClick={() => {
                  setCartItems([
                    ...cartItems,
                    {
                      skuType: selectedSKUType,
                      eligibleForDiscount:
                        isNextItemEligibleForFirstBagDiscount && purchaseType === PurchaseType.Autoship,
                      purchaseType: purchaseType!,
                      frequency: autoshipFrequency,
                    },
                  ]);
                }}
              >
                {t('add_to_cart')}
              </button>
            </div>
          }
          onPurchase={() => {
            purchaseMutation({
              variables: {
                input: {
                  skusToAdd: getCartSKUs(cartItems),
                },
              },
            }).catch((error: Error) => {
              // Catch billing errors and recommend a billing update change
              if (error.message.includes(RECURLY_ERROR_ADDENDUM)) {
                // Recurly billing errors are formatted as: "Error: [error message]. Please use a different card or contact your bank."
                // Send the error message without the preamble and addendum to the billing error state.
                setBillingError(error.message.replace('Error: ', '').replace(RECURLY_ERROR_ADDENDUM, ''));
              }
            });
          }}
          onRemoveItem={handleRemoveItem}
        />
      )}
      {showToast && !!toastChange && <Toast change={toastChange} multipleSubscriptionsChanged={false} />}
    </>
  );
}
